import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Smile, ArrowUp, Edit, Trash2, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

import { dentalIcons } from "../../dentail-indetifcation/utils-icons/dentalIcons";
import moment from "moment";
import { Input, Tooltip } from "antd";

function clampToZoomRoot(pos, w, h, pad = 8) {
  const zr = document.getElementById("zoom-root")?.getBoundingClientRect();
  if (!zr || !pos) return { left: pos?.x || 0, top: pos?.y || 0 };
  const minL = zr.left + pad;
  const minT = zr.top + pad;
  const maxL = zr.right - pad - (w || 0);
  const maxT = zr.bottom - pad - (h || 0);
  return {
    left: Math.max(minL, Math.min(pos.x, maxL)),
    top: Math.max(minT, Math.min(pos.y, maxT)),
  };
}

export function CommentThread({
  comment,
  position,
  onClose,
  onResolve,
  onDelete,
  onEditComment,
  onAddReply,
  isVisible,
}) {
  const [replyText, setReplyText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editText, setEditText] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const [showReply, setShowReply] = useState(false);

  if (!isVisible) return null;

  const handleReply = () => {
    if (replyText.trim()) {
      onAddReply(comment.id, replyText.trim());
      setReplyText("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  const handleEditStart = (id, currentText, isReply = false) => {
    if (isReply) {
      setEditingReplyId(id);
    } else {
      setEditingCommentId(id);
    }
    setEditText(currentText);
  };

  const handleEditSave = () => {
    if (editText.trim()) {
      if (editingReplyId) {
        onEditComment(editingReplyId, editText.trim(), true, comment.id);
        setEditingReplyId(null);
      } else if (editingCommentId) {
        onEditComment(editingCommentId, editText.trim());
        setEditingCommentId(null);
      }
      setEditText("");
    }
  };

  const handleEditCancel = () => {
    setEditingCommentId(null);
    setEditingReplyId(null);
    setEditText("");
  };

  const size = { w: 380, h: 420 };
  const { left, top } = clampToZoomRoot(position, size.w, size.h);

  return (
    <div
      className="image_analysis_container absolute z-50"
      // style={{
      //   left: Math.min(position.x + 200, window.innerWidth - 420), // Closer to click, but ensure it fits
      //   top: Math.max(position.y - 50, 20), // Closer to click, but ensure it's visible
      // }}
      style={{
        position: "fixed",
        left,
        top: 100,
        width: size.w,
        height: size.h,
      }}
    >
      <div
        className="bg-white text-black rounded-[16px] w-96 max-h-[600px]"
        style={{
          boxShadow:
            "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
        }}
      >
        {/* Header */}
        <div className="flex ps-3 items-center justify-between p-2 border-b border-[#EAEFF6]">
          <h3 className="text-lg font-medium text-[#000000]">Comment</h3>
          <div className="flex items-center gap-2">
            <Tooltip
              title={<span style={{ color: "#000" }}>Resolve</span>}
              color="white"
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onResolve(comment.id)}
                className="h-8 w-8 p-0 text-gray-400 hover:text-green-400"
              >
                <img src={dentalIcons.resolveIcon} alt="resolve" />
              </Button>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                >
                  {/* <MoreHorizontal className="w-4 h-4" /> */}
                  <img src={dentalIcons.dotsIcon} alt="menu" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="bg-white border-gray-700"
              >
                <DropdownMenuItem
                  onClick={() => onDelete(comment.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-gray-700 cursor-pointer"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete thread...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 text-gray-400 hover:text-white"
            >
              <img src={dentalIcons.closeIcon} alt="close" />
            </Button>
          </div>
        </div>

        {/* Comments */}
        <div className="p-3">
          {/* Main Comment */}
          <div className="mb-2">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex flex-col">
                    <span className="text-[16px] font-medium text-[#000000]">
                      {comment.author}
                    </span>
                    <span className="text-xs text-gray-400">
                      {moment(comment.timestamp).fromNow()}
                    </span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-gray-400 hover:text-white ml-auto"
                      >
                        <img src={dentalIcons.dotsIcon} alt="menu" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-white border-gray-700"
                    >
                      <DropdownMenuItem
                        onClick={() =>
                          handleEditStart(comment.id, comment.text)
                        }
                        className="text-[#323538]  hover:bg-gray-700 cursor-pointer"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {editingCommentId === comment.id ? (
                  <div className="space-y-3">
                    <Input.TextArea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Edit comment..."
                      autoSize={{ minRows: 2, maxRows: 10 }}
                      className="!rounded-[8px]"
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center w-full justify-between gap-2">
                        <div
                          onClick={handleEditCancel}
                          className="bg-white cursor-pointer border font-semibold h-[32px] w-[80px] text-[#8C8C8C] rounded-[7px] flex items-center justify-center text-[16px]"
                        >
                          Cancel
                        </div>
                        <Button
                          onClick={handleEditSave}
                          disabled={!editText.trim()}
                          size="sm"
                          className="bg-[#20C3FF] h-[32px] w-[80px] disabled:opacity-50"
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#535353]">{comment.text}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span
                className="text-md underline text-[#535353] cursor-pointer font-medium"
                onClick={() => setShowReply(true)}
              >
                REPLY{" "}
              </span>
              {comment.replies && comment.replies.length > 0 && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReplies(!showReplies);
                  }}
                  className="text-md text-[#8C8C8C] cursor-pointer font-semibold flex gap-2 items-center"
                >
                  {comment.replies.length}{" "}
                  {comment.replies.length === 1 ? "Reply" : "Replies"}
                  <ChevronDown
                    className={`h-5 w-5 transform transition-transform duration-200 ${
                      showReplies ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Replies */}
          {comment.replies && comment.replies.length > 0 && (
            <div className="space-y-3 border-gray-700 pl-4 max-h-[300px] overflow-scroll overflow-y-auto pr-2">
              {comment.replies.map((reply) => (
                <div
                  key={reply.id}
                  className="flex items-start gap-3 bg-[#F5F7FA] rounded-[12px] p-2"
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex flex-col">
                        <span className="text-[14px] font-medium text-[#000000]">
                          {comment.author}
                        </span>
                        <span className="text-xs font-medium text-[#8C8C8C]">
                          {moment(comment.timestamp).fromNow()}
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 text-gray-400 hover:text-white ml-auto"
                          >
                            <img src={dentalIcons.dotsIcon} alt="menu" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-white border-gray-700"
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              handleEditStart(reply.id, reply.text, true)
                            }
                            className="text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {editingReplyId === reply.id ? (
                      <div className="space-y-3">
                        <Input.TextArea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          placeholder="Edit reply..."
                          autoSize={{ minRows: 2, maxRows: 10 }}
                          className="!rounded-[8px]"
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2 items-center justify-between ">
                            <div
                              onClick={handleEditCancel}
                              className="bg-white border font-semibold h-[32px] w-[80px] text-[#8C8C8C] rounded-[7px] flex items-center justify-center text-[16px]"
                            >
                              Cancel
                            </div>
                          </div>

                          <Button
                            onClick={handleEditSave}
                            disabled={!editText.trim()}
                            size="sm"
                            className="bg-[#20C3FF] h-[32px] w-[80px] disabled:opacity-50 text-[16px] font-medium"
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[#606060]">{reply.text}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reply Input */}
          {showReply && (
            <div className="flex items-start gap-3 mt-4">
              <div className="flex-1 flex flex-col items-center gap-2">
                <Input.TextArea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoSize={{ minRows: 2, maxRows: 10 }} // 👈 auto-resizing
                  placeholder={`Reply @${comment.author}`}
                  className="!rounded-[8px]"
                />
                <div className="flex items-center justify-between w-full">
                  <div className="flex gap-2 items-center justify-between ">
                    <div
                      onClick={() => setShowReply(false)}
                      className="bg-white border cursor-pointer font-semibold h-[32px] w-[80px] text-[#8C8C8C] rounded-[7px] flex items-center justify-center text-[16px]"
                    >
                      Cancel
                    </div>
                  </div>

                  <Button
                    onClick={handleReply}
                    disabled={!replyText.trim()}
                    size="sm"
                    className="bg-[#20C3FF] h-[32px] w-[80px] disabled:opacity-50 text-[16px] font-medium"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
